import * as _ from 'underscore';

import { FeatureFlags } from '../../../ts/FeatureFlags';
import { ContentMapper } from '../../../ts/mappers/ContentMapper';
import { LocationMapper } from '../../../ts/mappers/LocationMapper';
import { MapperHelper } from '../../../ts/mappers/MapperHelper';
import { ProjectMapper } from '../../../ts/mappers/ProjectMapper';
import { UserMapper } from '../../../ts/mappers/UserMapper';
import { ILocationInfo } from '../../../ts/models/ILocationInfo';
import { IUserInfo } from '../../../ts/models/IUserInfo';
import { ICertifiableItemInfo,INamedItemInfo } from '../../../ts/models/resourceInfoParts';
import { IDatasourceUsageInfo } from '../../../ts/ServerApi/types/IUsageInfo';
import { WorkbookMapper } from '../../../workbook/models/mappers/WorkbookMapper';
import { IBasicDataSource, IDataSource, IDataSourceDetail } from '../../ServerApi/types/IDataSource';
import { IDataSourceBasicInfo, IDataSourceDetailInfo, IDataSourceInfo } from '../IDataSourceInfo';

export module DatasourceMapper {

  const M = MapperHelper;

  export function mapBasic(
    serverApiDatasources: IBasicDataSource[],
    users: _.Dictionary<IUserInfo>,
    projects: _.Dictionary<INamedItemInfo> = {},
    workbooks: _.Dictionary<INamedItemInfo> = {},
  ): IDataSourceBasicInfo[] {
    const datasourcesToProject = ContentMapper.getContentToProjectMap(ContentMapper.ContentTypeToTargetMap.datasource, projects);
    return _.map(serverApiDatasources, (ds: IBasicDataSource) => {
      const dsInfo: IDataSourceBasicInfo = {
        id:                        ds.id,
        luid:                      ds.luid,
        name:                      ds.name,
        owner:                     users[ds.ownerId],
        modifiedBy:                users[ds.modifiedByUserId],
        project:                   (FeatureFlags.refactorProjectEnabled) ? datasourcesToProject[ds.id] : projects[ds.projectId],
        workbook:                  workbooks[ds.workbookId],
      };
      return dsInfo;
    });
  }

  export function map(
    serverApiDatasources: IDataSource[],
    users: _.Dictionary<IUserInfo>,
    projects: _.Dictionary<INamedItemInfo> = {},
    locations: ILocationInfo[] = [],
    workbooks: _.Dictionary<INamedItemInfo> = {},
    usageInfoMaxValues?: IDatasourceUsageInfo,
    favoriteDatasources: _.Dictionary<any> = {}
  ): IDataSourceInfo[] {
    const datasourcesToProject = ContentMapper.getContentToProjectMap(ContentMapper.ContentTypeToTargetMap.datasource, projects);
    const datasourcesToLocation = ContentMapper.getContentToLocationMap(ContentMapper.ContentTypeToTargetMap.datasource, locations);
    return _.map(serverApiDatasources, (ds: IDataSource) => {

      let lastRefreshTime: Date;
      let psExtracts  = ds.publishedDatasourceDetails? ds.publishedDatasourceDetails.hasExtracts : false;
      let esExtracts  = ds.hasExtracts;
      let allExtracts = psExtracts && esExtracts;

      if(allExtracts){
          if(M.toDate(ds.publishedDatasourceDetails.lastRefreshedAt).valueOf() > M.toDate(ds.lastRefreshedAt).valueOf()){
            lastRefreshTime = M.toDate(ds.lastRefreshedAt);
          }
          else{
            lastRefreshTime = M.toDate(ds.publishedDatasourceDetails.lastRefreshedAt);
          }
      }
      else{
          lastRefreshTime = psExtracts? M.toDate(ds.publishedDatasourceDetails.lastRefreshedAt) :  M.toDate(ds.lastRefreshedAt);
      }

       const dsInfo: IDataSourceInfo = {
        connectedWorkbooksCount:   ds.connectedWorkbooksCount,
        connectionDetails:         ds.connectionDetails,
        connectionType:            ds.connectionType,
        connectionTypeDisplayName: ds.connectionTypeDisplayName,
        connectionTypeEditable:    ds.connectionTypeEditable,
        connectionOAuth:           ds.connectionOAuth,
        dataConnectionsCount:      ds.dataConnectionsCount,
        downloadUrl:               ds.downloadUrl,
        extractCreationPending:    ds.extractCreationPending,
        extractEncryptionState:    ds.extractEncryptionState,
        favorite:                  !!favoriteDatasources[ds.id],
        hasAlert:                  ds.hasAlert,
        hasExtracts:               ds.hasExtracts,
        publishedDatasourceDetails: ds.publishedDatasourceDetails,
        hasIncrementalExtract:     ds.hasIncrementalExtract,
        id:                        ds.id,
        idForFavoriting:           ds.datasourceId,
        isHierarchical:            ds.isHierarchical,
        lastRefreshedAt:           M.toDate(ds.lastRefreshedAt),
        dataFreshness:             lastRefreshTime,
        luid:                      ds.luid,
        name:                      ds.name,
        owner:                     UserMapper.mapOne(users[ds.ownerId]),
        modifiedBy:                UserMapper.mapOne(users[ds.modifiedByUserId]),
        project:                   (FeatureFlags.refactorProjectEnabled) ? datasourcesToProject[ds.datasourceId] : projects[ds.projectId],
        location:                  (FeatureFlags.personalSpaceEnabled) ? datasourcesToLocation[ds.datasourceId] : undefined,
        tags:                      M.sortTags(ds.tags),
        updatedAt:                 M.toDate(ds.updatedAt),
        urlName:                   ds.repositoryUrl,
        usageInfo:                 ds.usageInfo || <IDatasourceUsageInfo>{},
        usageInfoMaxValues:        usageInfoMaxValues || <IDatasourceUsageInfo>{},
        workbook:                  workbooks[ds.workbookId],
        certificationDetails:      ds.certificationDetails,
        dataQualityIndicators:     ds.dataQualityIndicators || [],
        contentType:               'datasource',
        description:               ds.description,
        hasEncryptableExtracts:    ds.hasEncryptableExtracts,
        askDataIndexing:           ds.askDataIndexing,
        testingConnectionAllowed:  ds.testingConnectionAllowed,
        usingRemoteQueryAgent:     ds.usingRemoteQueryAgent,
        isExtractOnly:             ds.isExtractOnly,
        isFileBased:               ds.isFileBased
      };

      return dsInfo;
    });
  }

  export function mapDetail(ds: IDataSourceDetail): IDataSourceDetailInfo {
    const owner = UserMapper.mapOne(ds.owner);
    const modifiedBy = UserMapper.mapOne(ds.modifiedByUser);
    const project = ds.project ? ProjectMapper.mapName(ds.project) : null;

    const dsInfo: IDataSourceDetailInfo = {
      connectedUsersCount:         ds.connectedUsersCount,
      connectedWorkbooksCount:     ds.connectedWorkbooksCount,
      publishedDatasourceDetails:  ds.publishedDatasourceDetails,
      connectionDetails:           ds.connectionDetails,
      connectionType:              ds.connectionType,
      connectionTypeDisplayName:   ds.connectionTypeDisplayName,
      connectionTypeEditable:      ds.connectionTypeEditable,
      connectionOAuth:             ds.connectionOAuth,
      createdAt:                   M.toDate(ds.createdAt),
      dataConnectionsCount:        ds.dataConnectionsCount,
      description:                 ds.description,
      downloadUrl:                 ds.downloadUrl,
      extractCreationPending:      ds.extractCreationPending,
      extractEncryptionState:      ds.extractEncryptionState,
      favorite:                    ds.favorite,
      hasAlert:                    ds.hasAlert,
      hasExtracts:                 ds.hasExtracts,
      hasIncrementalExtract:       ds.hasIncrementalExtract,
      id:                          ds.id,
      idForFavoriting:             ds.datasourceId,
      isHierarchical:              ds.isHierarchical,
      lastRefreshedAt:             M.toDate(ds.lastRefreshedAt),
      luid:                        ds.luid,
      name:                        ds.name,
      owner:                       owner,
      modifiedBy:                  modifiedBy,
      project:                     project,
      refreshMode:                 ds.refreshMode,
      remoteAgentName:             ds.remoteAgentName,
      remoteAgentTimeZone:         ds.remoteAgentTimeZone,
      remoteAgentReservationId:    ds.remoteAgentReservationId,
      remoteRunNextAt:             ds.remoteRunNextAt,
      remoteQueryAgentSiteDefault: ds.remoteQueryAgentSiteDefault,
      tags:                        M.sortTags(ds.tags),
      updatedAt:                   M.toDate(ds.updatedAt),
      urlName:                     ds.repositoryUrl,
      certificationDetails:        ds.certificationDetails,
      dataQualityIndicators: ds.dataQualityIndicators || [],
      testingConnectionAllowed:    ds.testingConnectionAllowed,
      usingRemoteQueryAgent:       ds.usingRemoteQueryAgent,
      contentType:                 'datasource',
      hasEncryptableExtracts:      ds.hasEncryptableExtracts,
      askDataIndexing:             ds.askDataIndexing,
      isExtractOnly:               ds.isExtractOnly,
      isFileBased:                 ds.isFileBased,
    };

    if(ds.workbook) dsInfo.workbook = WorkbookMapper.mapOne(ds.workbook, owner, modifiedBy, project);

    return dsInfo;
  }

  export function mapFromCRASS(content: any): IDataSourceInfo{
      return {
        connectedWorkbooksCount:   content.connectedWorkbooksCount,
        connectionDetails:         null,
        connectionType:            content.connectionType,
        connectionTypeDisplayName: content.connectionType, // missing proper value
        connectionTypeEditable:    false,
        connectionOAuth:           null,
        datasourceLuid:            content.datasourceLuid,
        dataConnectionsCount:      content.dataconnectionsCount,
        downloadUrl:               null, // missing site prefix
        extractCreationPending:    content.extractCreationPending,
        extractEncryptionState:    content.extractEncryptionState,
        favorite:                  false,
        hasAlert:                  content.hasAlert !== 'none', // missing additional visibility checks
        hasExtracts:               content.hasExtracts,
        hasIncrementalExtract:     content.hasIncrementalExtract,
        id:                        content.id + '',
        idForFavoriting:           content.datasourceId + '',
        isHierarchical:            content.isHierarchical,
        lastRefreshedAt:           M.toDate(content.extractUpdatedAt) || M.toDateFromTimeOffsetObject(content.extractUpdatedAt),
        luid:                      content.luid,
        name:                      content.title,
        tags:                      M.sortTags(content.tags),
        updatedAt:                 M.toDate(content.modifiedTime) || M.toDateFromTimeOffsetObject(content.modifiedTime),
        urlName:                   content.repositoryUrl,
        usageInfoMaxValues:        <IDatasourceUsageInfo>{},
        workbook:                  null, //missing
        certificationDetails:      <ICertifiableItemInfo>{
            isCertified:              content.isCertified,
            certifier:                content.isCertified ? UserMapper.mapOneCertifierFromCRASS(content) : null,
            certificationNote:        content.isCertified ? content.certificationNote : ''
        },
        dataQualityIndicators:     [], // missing
        contentType:               'datasource',
        description:               content.description,
        hasEncryptableExtracts:    content.hasEncryptableExtracts,
        askDataIndexing:           null, // missing
        testingConnectionAllowed:  !content.usingRemoteQueryAgent,
        usingRemoteQueryAgent:     content.usingRemoteQueryAgent,
        isExtractOnly:             content.isExtractOnly,
        isFileBased:               content.isFileBased,
        project:                   {
          id: content.projectId ?? content.parentProjectId,
          name: content.containerName
        },
        usageInfo:                 <IDatasourceUsageInfo>{
            ...MapperHelper.mapCRASSUsageInfo(content)
        },
        owner: UserMapper.mapOneOwnerFromCRASS(content),
        location: LocationMapper.mapLocationFromCrass(content)
      };
  }
}

